// The selected App's sources and refresh time (editors, Versions tab): what it reads, renewing that access, and
// the daily refresh time. Namespace `app-source-plan`; owner: T2-UI1; all 24 languages from DES2
// (org/work/DES2/handoff/app-source-plan-24.json). Placeholders are `{name}`, filled with `formatCopy`.
import type { NamespaceCatalog } from '@/i18n/ns';

const en = {
  heading: 'Sources',
  none: 'This version reads no sources.',
  renew: 'Renew access',
  refreshHeading: 'Refresh time',
  refreshDaily: 'Every day at {time} ({zone})',
  timeLabel: 'Time',
  zoneLabel: 'Time zone',
  refreshSave: 'Save refresh time',
  working: 'Saving…',
  saved: 'Saved. Opening the new version…',
  failed: 'That didn’t work. Nothing was changed. Try again.',
  unconfirmed: 'We couldn’t confirm it was saved.',
  checkAgain: 'Check again',
  invalidTime: 'Choose a time and a time zone.',
  preview: 'Preview',
  previewHide: 'Hide preview',
  previewing: 'Loading the preview…',
  previewEmpty: 'The source returned no data.',
  previewDenied: 'You can’t read this source.',
  previewChanged: 'This version changed. Reopen it and try again.',
  previewUnavailable: 'The preview isn’t available right now.',
  previewMore: 'Showing the first rows only.',
  previewRetry: 'Try again',
};
export type AppSourcePlanCopyKey = keyof typeof en;
type Copy = Record<AppSourcePlanCopyKey, string>;

const bg: Copy = {
  heading: 'Източници',
  none: 'Тази версия не чете източници.',
  renew: 'Подновяване на достъпа',
  refreshHeading: 'Час на обновяване',
  refreshDaily: 'Всеки ден в {time} ({zone})',
  timeLabel: 'Час',
  zoneLabel: 'Часова зона',
  refreshSave: 'Запазване на часа на обновяване',
  working: 'Запазване…',
  saved: 'Запазено. Отваряне на новата версия…',
  failed: 'Не се получи. Нищо не е променено. Опитайте отново.',
  unconfirmed: 'Не успяхме да потвърдим, че е запазено.',
  checkAgain: 'Проверка отново',
  invalidTime: 'Изберете час и часова зона.',
  preview: 'Преглед',
  previewHide: 'Скриване на прегледа',
  previewing: 'Зареждане на прегледа…',
  previewEmpty: 'Източникът не върна данни.',
  previewDenied: 'Не можете да четете този източник.',
  previewChanged: 'Тази версия се промени. Отворете я отново и опитайте пак.',
  previewUnavailable: 'Прегледът в момента не е наличен.',
  previewMore: 'Показани са само първите редове.',
  previewRetry: 'Опитай отново',
};

const cs: Copy = {
  heading: 'Zdroje',
  none: 'Tato verze nečte žádné zdroje.',
  renew: 'Obnovit přístup',
  refreshHeading: 'Čas obnovení',
  refreshDaily: 'Každý den v {time} ({zone})',
  timeLabel: 'Čas',
  zoneLabel: 'Časové pásmo',
  refreshSave: 'Uložit čas obnovení',
  working: 'Ukládání…',
  saved: 'Uloženo. Otevírá se nová verze…',
  failed: 'Nepovedlo se. Nic se nezměnilo. Zkuste to znovu.',
  unconfirmed: 'Nepodařilo se ověřit, že je uloženo.',
  checkAgain: 'Zkontrolovat znovu',
  invalidTime: 'Zvolte čas a časové pásmo.',
  preview: 'Náhled',
  previewHide: 'Skrýt náhled',
  previewing: 'Načítání náhledu…',
  previewEmpty: 'Zdroj nevrátil žádná data.',
  previewDenied: 'Tento zdroj nemůžete číst.',
  previewChanged: 'Tato verze se změnila. Otevřete ji znovu a zkuste to ještě jednou.',
  previewUnavailable: 'Náhled teď není k dispozici.',
  previewMore: 'Zobrazují se jen první řádky.',
  previewRetry: 'Zkusit znovu',
};

const da: Copy = {
  heading: 'Kilder',
  none: 'Denne version læser ingen kilder.',
  renew: 'Forny adgang',
  refreshHeading: 'Opdateringstidspunkt',
  refreshDaily: 'Hver dag kl. {time} ({zone})',
  timeLabel: 'Tidspunkt',
  zoneLabel: 'Tidszone',
  refreshSave: 'Gem opdateringstidspunkt',
  working: 'Gemmer…',
  saved: 'Gemt. Åbner den nye version…',
  failed: 'Det lykkedes ikke. Intet blev ændret. Prøv igen.',
  unconfirmed: 'Vi kunne ikke bekræfte, at det blev gemt.',
  checkAgain: 'Tjek igen',
  invalidTime: 'Vælg et tidspunkt og en tidszone.',
  preview: 'Forhåndsvisning',
  previewHide: 'Skjul forhåndsvisning',
  previewing: 'Indlæser forhåndsvisningen…',
  previewEmpty: 'Kilden returnerede ingen data.',
  previewDenied: 'Du kan ikke læse denne kilde.',
  previewChanged: 'Denne version er ændret. Åbn den igen, og prøv igen.',
  previewUnavailable: 'Forhåndsvisningen er ikke tilgængelig lige nu.',
  previewMore: 'Viser kun de første rækker.',
  previewRetry: 'Prøv igen',
};

const de: Copy = {
  heading: 'Quellen',
  none: 'Diese Version liest keine Quellen.',
  renew: 'Zugriff erneuern',
  refreshHeading: 'Aktualisierungszeit',
  refreshDaily: 'Jeden Tag um {time} ({zone})',
  timeLabel: 'Uhrzeit',
  zoneLabel: 'Zeitzone',
  refreshSave: 'Aktualisierungszeit speichern',
  working: 'Wird gespeichert…',
  saved: 'Gespeichert. Die neue Version wird geöffnet…',
  failed: 'Das hat nicht geklappt. Es wurde nichts geändert. Versuchen Sie es erneut.',
  unconfirmed: 'Wir konnten nicht bestätigen, dass es gespeichert wurde.',
  checkAgain: 'Erneut prüfen',
  invalidTime: 'Wählen Sie eine Uhrzeit und eine Zeitzone.',
  preview: 'Vorschau',
  previewHide: 'Vorschau ausblenden',
  previewing: 'Vorschau wird geladen…',
  previewEmpty: 'Die Quelle hat keine Daten geliefert.',
  previewDenied: 'Sie können diese Quelle nicht lesen.',
  previewChanged:
    'Diese Version hat sich geändert. Öffnen Sie sie erneut und versuchen Sie es noch einmal.',
  previewUnavailable: 'Die Vorschau ist gerade nicht verfügbar.',
  previewMore: 'Es werden nur die ersten Zeilen angezeigt.',
  previewRetry: 'Erneut versuchen',
};

const el: Copy = {
  heading: 'Πηγές',
  none: 'Αυτή η έκδοση δεν διαβάζει πηγές.',
  renew: 'Ανανέωση πρόσβασης',
  refreshHeading: 'Ώρα ανανέωσης',
  refreshDaily: 'Κάθε μέρα στις {time} ({zone})',
  timeLabel: 'Ώρα',
  zoneLabel: 'Ζώνη ώρας',
  refreshSave: 'Αποθήκευση ώρας ανανέωσης',
  working: 'Αποθήκευση…',
  saved: 'Αποθηκεύτηκε. Άνοιγμα της νέας έκδοσης…',
  failed: 'Δεν ήταν δυνατό. Δεν άλλαξε τίποτα. Δοκιμάστε ξανά.',
  unconfirmed: 'Δεν μπορέσαμε να επιβεβαιώσουμε ότι αποθηκεύτηκε.',
  checkAgain: 'Έλεγχος ξανά',
  invalidTime: 'Επιλέξτε ώρα και ζώνη ώρας.',
  preview: 'Προεπισκόπηση',
  previewHide: 'Απόκρυψη προεπισκόπησης',
  previewing: 'Φόρτωση της προεπισκόπησης…',
  previewEmpty: 'Η πηγή δεν επέστρεψε δεδομένα.',
  previewDenied: 'Δεν μπορείτε να διαβάσετε αυτή την πηγή.',
  previewChanged: 'Αυτή η έκδοση άλλαξε. Ανοίξτε την ξανά και δοκιμάστε πάλι.',
  previewUnavailable: 'Η προεπισκόπηση δεν είναι διαθέσιμη αυτή τη στιγμή.',
  previewMore: 'Εμφανίζονται μόνο οι πρώτες γραμμές.',
  previewRetry: 'Δοκιμή ξανά',
};

const es: Copy = {
  heading: 'Fuentes',
  none: 'Esta versión no lee ninguna fuente.',
  renew: 'Renovar acceso',
  refreshHeading: 'Hora de actualización',
  refreshDaily: 'Todos los días a las {time} ({zone})',
  timeLabel: 'Hora',
  zoneLabel: 'Zona horaria',
  refreshSave: 'Guardar hora de actualización',
  working: 'Guardando…',
  saved: 'Guardado. Abriendo la nueva versión…',
  failed: 'No ha funcionado. No se ha cambiado nada. Inténtalo de nuevo.',
  unconfirmed: 'No pudimos confirmar que se guardara.',
  checkAgain: 'Comprobar de nuevo',
  invalidTime: 'Elige una hora y una zona horaria.',
  preview: 'Vista previa',
  previewHide: 'Ocultar vista previa',
  previewing: 'Cargando la vista previa…',
  previewEmpty: 'La fuente no devolvió datos.',
  previewDenied: 'No puedes leer esta fuente.',
  previewChanged: 'Esta versión ha cambiado. Vuelve a abrirla e inténtalo de nuevo.',
  previewUnavailable: 'La vista previa no está disponible ahora mismo.',
  previewMore: 'Solo se muestran las primeras filas.',
  previewRetry: 'Volver a intentar',
};

const et: Copy = {
  heading: 'Allikad',
  none: 'See versioon ei loe ühtegi allikat.',
  renew: 'Uuenda juurdepääsu',
  refreshHeading: 'Värskendamise aeg',
  refreshDaily: 'Iga päev kell {time} ({zone})',
  timeLabel: 'Aeg',
  zoneLabel: 'Ajavöönd',
  refreshSave: 'Salvesta värskendamise aeg',
  working: 'Salvestamine…',
  saved: 'Salvestatud. Uus versioon avaneb…',
  failed: 'See ei õnnestunud. Midagi ei muudetud. Proovi uuesti.',
  unconfirmed: 'Me ei saanud kinnitada, et see salvestati.',
  checkAgain: 'Kontrolli uuesti',
  invalidTime: 'Vali aeg ja ajavöönd.',
  preview: 'Eelvaade',
  previewHide: 'Peida eelvaade',
  previewing: 'Eelvaate laadimine…',
  previewEmpty: 'Allikas ei tagastanud andmeid.',
  previewDenied: 'Sa ei saa seda allikat lugeda.',
  previewChanged: 'See versioon muutus. Ava see uuesti ja proovi veel kord.',
  previewUnavailable: 'Eelvaade pole praegu saadaval.',
  previewMore: 'Näidatakse ainult esimesi ridu.',
  previewRetry: 'Proovi uuesti',
};

const fi: Copy = {
  heading: 'Lähteet',
  none: 'Tämä versio ei lue lähteitä.',
  renew: 'Uusi käyttöoikeus',
  refreshHeading: 'Päivitysaika',
  refreshDaily: 'Joka päivä klo {time} ({zone})',
  timeLabel: 'Aika',
  zoneLabel: 'Aikavyöhyke',
  refreshSave: 'Tallenna päivitysaika',
  working: 'Tallennetaan…',
  saved: 'Tallennettu. Uusi versio avautuu…',
  failed: 'Se ei onnistunut. Mitään ei muutettu. Yritä uudelleen.',
  unconfirmed: 'Emme voineet vahvistaa, että se tallentui.',
  checkAgain: 'Tarkista uudelleen',
  invalidTime: 'Valitse aika ja aikavyöhyke.',
  preview: 'Esikatselu',
  previewHide: 'Piilota esikatselu',
  previewing: 'Ladataan esikatselua…',
  previewEmpty: 'Lähde ei palauttanut tietoja.',
  previewDenied: 'Et voi lukea tätä lähdettä.',
  previewChanged: 'Tämä versio muuttui. Avaa se uudelleen ja yritä uudestaan.',
  previewUnavailable: 'Esikatselu ei ole juuri nyt käytettävissä.',
  previewMore: 'Näytetään vain ensimmäiset rivit.',
  previewRetry: 'Yritä uudelleen',
};

const fr: Copy = {
  heading: 'Sources',
  none: 'Cette version ne lit aucune source.',
  renew: 'Renouveler l’accès',
  refreshHeading: 'Heure d’actualisation',
  refreshDaily: 'Tous les jours à {time} ({zone})',
  timeLabel: 'Heure',
  zoneLabel: 'Fuseau horaire',
  refreshSave: 'Enregistrer l’heure d’actualisation',
  working: 'Enregistrement…',
  saved: 'Enregistré. Ouverture de la nouvelle version…',
  failed: 'Cela n’a pas fonctionné. Rien n’a été modifié. Réessayez.',
  unconfirmed: 'Nous n’avons pas pu confirmer l’enregistrement.',
  checkAgain: 'Vérifier à nouveau',
  invalidTime: 'Choisissez une heure et un fuseau horaire.',
  preview: 'Aperçu',
  previewHide: 'Masquer l’aperçu',
  previewing: 'Chargement de l’aperçu…',
  previewEmpty: 'La source n’a renvoyé aucune donnée.',
  previewDenied: 'Vous ne pouvez pas lire cette source.',
  previewChanged: 'Cette version a changé. Rouvrez-la et réessayez.',
  previewUnavailable: 'L’aperçu n’est pas disponible pour le moment.',
  previewMore: 'Seules les premières lignes sont affichées.',
  previewRetry: 'Réessayer',
};

const ga: Copy = {
  heading: 'Foinsí',
  none: 'Ní léann an leagan seo aon fhoinse.',
  renew: 'Athnuaigh rochtain',
  refreshHeading: 'Am athnuachana',
  refreshDaily: 'Gach lá ag {time} ({zone})',
  timeLabel: 'Am',
  zoneLabel: 'Crios ama',
  refreshSave: 'Sábháil am athnuachana',
  working: 'Ag sábháil…',
  saved: 'Sábháilte. Ag oscailt an leagain nua…',
  failed: 'Níor oibrigh sé sin. Níor athraíodh aon rud. Bain triail eile as.',
  unconfirmed: 'Níorbh fhéidir linn a dheimhniú gur sábháladh é.',
  checkAgain: 'Seiceáil arís',
  invalidTime: 'Roghnaigh am agus crios ama.',
  preview: 'Réamhamharc',
  previewHide: 'Folaigh an réamhamharc',
  previewing: 'Ag lódáil an réamhamhairc…',
  previewEmpty: 'Níor sheol an fhoinse aon sonraí ar ais.',
  previewDenied: 'Ní féidir leat an fhoinse seo a léamh.',
  previewChanged: 'Athraíodh an leagan seo. Oscail arís é agus bain triail eile as.',
  previewUnavailable: 'Níl an réamhamharc ar fáil faoi láthair.',
  previewMore: 'Na chéad sraitheanna amháin atá á dtaispeáint.',
  previewRetry: 'Triail arís',
};

const hr: Copy = {
  heading: 'Izvori',
  none: 'Ova verzija ne čita nijedan izvor.',
  renew: 'Obnovi pristup',
  refreshHeading: 'Vrijeme osvježavanja',
  refreshDaily: 'Svaki dan u {time} ({zone})',
  timeLabel: 'Vrijeme',
  zoneLabel: 'Vremenska zona',
  refreshSave: 'Spremi vrijeme osvježavanja',
  working: 'Spremanje…',
  saved: 'Spremljeno. Otvara se nova verzija…',
  failed: 'Nije uspjelo. Ništa nije promijenjeno. Pokušajte ponovno.',
  unconfirmed: 'Nismo mogli potvrditi da je spremljeno.',
  checkAgain: 'Provjeri ponovno',
  invalidTime: 'Odaberite vrijeme i vremensku zonu.',
  preview: 'Pretpregled',
  previewHide: 'Sakrij pretpregled',
  previewing: 'Učitavanje pretpregleda…',
  previewEmpty: 'Izvor nije vratio podatke.',
  previewDenied: 'Ne možete čitati ovaj izvor.',
  previewChanged: 'Ova se verzija promijenila. Ponovno je otvorite i pokušajte opet.',
  previewUnavailable: 'Pretpregled trenutačno nije dostupan.',
  previewMore: 'Prikazuju se samo prvi retci.',
  previewRetry: 'Pokušaj ponovno',
};

const hu: Copy = {
  heading: 'Források',
  none: 'Ez a verzió nem olvas forrásokat.',
  renew: 'Hozzáférés megújítása',
  refreshHeading: 'Frissítési időpont',
  refreshDaily: 'Minden nap {time}-kor ({zone})',
  timeLabel: 'Időpont',
  zoneLabel: 'Időzóna',
  refreshSave: 'Frissítési időpont mentése',
  working: 'Mentés…',
  saved: 'Mentve. Az új verzió megnyitása…',
  failed: 'Ez nem sikerült. Semmi sem változott. Próbálja újra.',
  unconfirmed: 'Nem tudtuk megerősíteni, hogy a mentés megtörtént.',
  checkAgain: 'Ellenőrzés újra',
  invalidTime: 'Válasszon időpontot és időzónát.',
  preview: 'Előnézet',
  previewHide: 'Előnézet elrejtése',
  previewing: 'Az előnézet betöltése…',
  previewEmpty: 'A forrás nem adott vissza adatot.',
  previewDenied: 'Ezt a forrást nem olvashatja.',
  previewChanged: 'Ez a verzió megváltozott. Nyissa meg újra, és próbálja újra.',
  previewUnavailable: 'Az előnézet jelenleg nem érhető el.',
  previewMore: 'Csak az első sorok láthatók.',
  previewRetry: 'Újrapróbálás',
};

const it: Copy = {
  heading: 'Fonti',
  none: 'Questa versione non legge alcuna fonte.',
  renew: 'Rinnova accesso',
  refreshHeading: 'Orario di aggiornamento',
  refreshDaily: 'Ogni giorno alle {time} ({zone})',
  timeLabel: 'Ora',
  zoneLabel: 'Fuso orario',
  refreshSave: 'Salva orario di aggiornamento',
  working: 'Salvataggio…',
  saved: 'Salvato. Apertura della nuova versione…',
  failed: 'Non ha funzionato. Non è stato modificato nulla. Riprova.',
  unconfirmed: 'Non siamo riusciti a confermare il salvataggio.',
  checkAgain: 'Controlla di nuovo',
  invalidTime: 'Scegli un orario e un fuso orario.',
  preview: 'Anteprima',
  previewHide: 'Nascondi anteprima',
  previewing: 'Caricamento dell’anteprima…',
  previewEmpty: 'La fonte non ha restituito dati.',
  previewDenied: 'Non puoi leggere questa fonte.',
  previewChanged: 'Questa versione è cambiata. Riaprila e riprova.',
  previewUnavailable: 'L’anteprima non è disponibile al momento.',
  previewMore: 'Sono mostrate solo le prime righe.',
  previewRetry: 'Riprova',
};

const lt: Copy = {
  heading: 'Šaltiniai',
  none: 'Ši versija neskaito jokių šaltinių.',
  renew: 'Atnaujinti prieigą',
  refreshHeading: 'Atnaujinimo laikas',
  refreshDaily: 'Kasdien {time} ({zone})',
  timeLabel: 'Laikas',
  zoneLabel: 'Laiko juosta',
  refreshSave: 'Išsaugoti atnaujinimo laiką',
  working: 'Saugoma…',
  saved: 'Išsaugota. Atidaroma nauja versija…',
  failed: 'Nepavyko. Niekas nepakeista. Bandykite dar kartą.',
  unconfirmed: 'Nepavyko patvirtinti, kad išsaugota.',
  checkAgain: 'Tikrinti dar kartą',
  invalidTime: 'Pasirinkite laiką ir laiko juostą.',
  preview: 'Peržiūra',
  previewHide: 'Slėpti peržiūrą',
  previewing: 'Įkeliama peržiūra…',
  previewEmpty: 'Šaltinis negrąžino duomenų.',
  previewDenied: 'Negalite skaityti šio šaltinio.',
  previewChanged: 'Ši versija pasikeitė. Atidarykite ją iš naujo ir bandykite dar kartą.',
  previewUnavailable: 'Peržiūra šiuo metu nepasiekiama.',
  previewMore: 'Rodomos tik pirmosios eilutės.',
  previewRetry: 'Bandyti dar kartą',
};

const lv: Copy = {
  heading: 'Avoti',
  none: 'Šī versija nelasa nevienu avotu.',
  renew: 'Atjaunot piekļuvi',
  refreshHeading: 'Atsvaidzināšanas laiks',
  refreshDaily: 'Katru dienu plkst. {time} ({zone})',
  timeLabel: 'Laiks',
  zoneLabel: 'Laika josla',
  refreshSave: 'Saglabāt atsvaidzināšanas laiku',
  working: 'Notiek saglabāšana…',
  saved: 'Saglabāts. Tiek atvērta jaunā versija…',
  failed: 'Neizdevās. Nekas netika mainīts. Mēģiniet vēlreiz.',
  unconfirmed: 'Nevarējām apstiprināt, ka tas ir saglabāts.',
  checkAgain: 'Pārbaudīt vēlreiz',
  invalidTime: 'Izvēlieties laiku un laika joslu.',
  preview: 'Priekšskatījums',
  previewHide: 'Paslēpt priekšskatījumu',
  previewing: 'Ielādē priekšskatījumu…',
  previewEmpty: 'Avots neatgrieza datus.',
  previewDenied: 'Jūs nevarat lasīt šo avotu.',
  previewChanged: 'Šī versija ir mainījusies. Atveriet to vēlreiz un mēģiniet atkal.',
  previewUnavailable: 'Priekšskatījums pašlaik nav pieejams.',
  previewMore: 'Tiek rādītas tikai pirmās rindas.',
  previewRetry: 'Mēģināt vēlreiz',
};

const mt: Copy = {
  heading: 'Sorsi',
  none: 'Din il-verżjoni ma taqra l-ebda sors.',
  renew: 'Ġedded l-aċċess',
  refreshHeading: 'Ħin tal-aġġornament',
  refreshDaily: 'Kuljum fil-{time} ({zone})',
  timeLabel: 'Ħin',
  zoneLabel: 'Żona tal-ħin',
  refreshSave: 'Issejvja l-ħin tal-aġġornament',
  working: 'Qed jiġi salvat…',
  saved: 'Issejvjat. Qed tinfetaħ il-verżjoni l-ġdida…',
  failed: 'Ma ħadimx. Xejn ma nbidel. Erġa’ pprova.',
  unconfirmed: 'Ma stajniex nikkonfermaw li ġie ssejvjat.',
  checkAgain: 'Iċċekkja mill-ġdid',
  invalidTime: 'Agħżel ħin u żona tal-ħin.',
  preview: 'Previżjoni',
  previewHide: 'Aħbi l-previżjoni',
  previewing: 'Qed titgħabba l-previżjoni…',
  previewEmpty: 'Is-sors ma rritorna l-ebda dejta.',
  previewDenied: 'Ma tistax taqra dan is-sors.',
  previewChanged: 'Din il-verżjoni nbidlet. Erġa’ iftaħha u erġa’ pprova.',
  previewUnavailable: 'Il-previżjoni mhix disponibbli bħalissa.',
  previewMore: 'Qed jidhru l-ewwel ringieli biss.',
  previewRetry: 'Erġa’ pprova',
};

const nl: Copy = {
  heading: 'Bronnen',
  none: 'Deze versie leest geen bronnen.',
  renew: 'Toegang vernieuwen',
  refreshHeading: 'Vernieuwingstijd',
  refreshDaily: 'Elke dag om {time} ({zone})',
  timeLabel: 'Tijd',
  zoneLabel: 'Tijdzone',
  refreshSave: 'Vernieuwingstijd opslaan',
  working: 'Opslaan…',
  saved: 'Opgeslagen. De nieuwe versie wordt geopend…',
  failed: 'Dat is niet gelukt. Er is niets gewijzigd. Probeer het opnieuw.',
  unconfirmed: 'We konden niet bevestigen dat het is opgeslagen.',
  checkAgain: 'Opnieuw controleren',
  invalidTime: 'Kies een tijd en een tijdzone.',
  preview: 'Voorbeeld',
  previewHide: 'Voorbeeld verbergen',
  previewing: 'Het voorbeeld wordt geladen…',
  previewEmpty: 'De bron heeft geen gegevens teruggegeven.',
  previewDenied: 'Je kunt deze bron niet lezen.',
  previewChanged: 'Deze versie is gewijzigd. Open hem opnieuw en probeer het nog eens.',
  previewUnavailable: 'Het voorbeeld is op dit moment niet beschikbaar.',
  previewMore: 'Alleen de eerste rijen worden getoond.',
  previewRetry: 'Opnieuw proberen',
};

const pl: Copy = {
  heading: 'Źródła',
  none: 'Ta wersja nie odczytuje żadnych źródeł.',
  renew: 'Odnów dostęp',
  refreshHeading: 'Godzina odświeżania',
  refreshDaily: 'Codziennie o {time} ({zone})',
  timeLabel: 'Godzina',
  zoneLabel: 'Strefa czasowa',
  refreshSave: 'Zapisz godzinę odświeżania',
  working: 'Zapisywanie…',
  saved: 'Zapisano. Otwieranie nowej wersji…',
  failed: 'Nie udało się. Nic nie zostało zmienione. Spróbuj ponownie.',
  unconfirmed: 'Nie udało się potwierdzić, że zapisano.',
  checkAgain: 'Sprawdź ponownie',
  invalidTime: 'Wybierz godzinę i strefę czasową.',
  preview: 'Podgląd',
  previewHide: 'Ukryj podgląd',
  previewing: 'Wczytywanie podglądu…',
  previewEmpty: 'Źródło nie zwróciło żadnych danych.',
  previewDenied: 'Nie możesz odczytać tego źródła.',
  previewChanged: 'Ta wersja się zmieniła. Otwórz ją ponownie i spróbuj jeszcze raz.',
  previewUnavailable: 'Podgląd jest teraz niedostępny.',
  previewMore: 'Wyświetlane są tylko pierwsze wiersze.',
  previewRetry: 'Spróbuj ponownie',
};

const pt: Copy = {
  heading: 'Fontes',
  none: 'Esta versão não lê nenhuma fonte.',
  renew: 'Renovar acesso',
  refreshHeading: 'Hora de atualização',
  refreshDaily: 'Todos os dias às {time} ({zone})',
  timeLabel: 'Hora',
  zoneLabel: 'Fuso horário',
  refreshSave: 'Guardar hora de atualização',
  working: 'A guardar…',
  saved: 'Guardado. A abrir a nova versão…',
  failed: 'Não funcionou. Nada foi alterado. Tente novamente.',
  unconfirmed: 'Não foi possível confirmar que foi guardado.',
  checkAgain: 'Verificar novamente',
  invalidTime: 'Escolha uma hora e um fuso horário.',
  preview: 'Pré-visualização',
  previewHide: 'Ocultar pré-visualização',
  previewing: 'A carregar a pré-visualização…',
  previewEmpty: 'A fonte não devolveu dados.',
  previewDenied: 'Não pode ler esta fonte.',
  previewChanged: 'Esta versão mudou. Abra-a novamente e tente outra vez.',
  previewUnavailable: 'A pré-visualização não está disponível neste momento.',
  previewMore: 'Só são mostradas as primeiras linhas.',
  previewRetry: 'Tentar novamente',
};

const ro: Copy = {
  heading: 'Surse',
  none: 'Această versiune nu citește nicio sursă.',
  renew: 'Reînnoiește accesul',
  refreshHeading: 'Ora de actualizare',
  refreshDaily: 'În fiecare zi la {time} ({zone})',
  timeLabel: 'Ora',
  zoneLabel: 'Fus orar',
  refreshSave: 'Salvează ora de actualizare',
  working: 'Se salvează…',
  saved: 'Salvat. Se deschide noua versiune…',
  failed: 'Nu a funcționat. Nu s-a schimbat nimic. Încearcă din nou.',
  unconfirmed: 'Nu am putut confirma că a fost salvat.',
  checkAgain: 'Verifică din nou',
  invalidTime: 'Alege o oră și un fus orar.',
  preview: 'Previzualizare',
  previewHide: 'Ascunde previzualizarea',
  previewing: 'Se încarcă previzualizarea…',
  previewEmpty: 'Sursa nu a returnat date.',
  previewDenied: 'Nu poți citi această sursă.',
  previewChanged: 'Această versiune s-a schimbat. Redeschide-o și încearcă din nou.',
  previewUnavailable: 'Previzualizarea nu este disponibilă momentan.',
  previewMore: 'Se afișează doar primele rânduri.',
  previewRetry: 'Încearcă din nou',
};

const sk: Copy = {
  heading: 'Zdroje',
  none: 'Táto verzia nečíta žiadne zdroje.',
  renew: 'Obnoviť prístup',
  refreshHeading: 'Čas obnovenia',
  refreshDaily: 'Každý deň o {time} ({zone})',
  timeLabel: 'Čas',
  zoneLabel: 'Časové pásmo',
  refreshSave: 'Uložiť čas obnovenia',
  working: 'Ukladá sa…',
  saved: 'Uložené. Otvára sa nová verzia…',
  failed: 'Nepodarilo sa. Nič sa nezmenilo. Skúste to znova.',
  unconfirmed: 'Nepodarilo sa overiť, že je uložené.',
  checkAgain: 'Skontrolovať znova',
  invalidTime: 'Zvoľte čas a časové pásmo.',
  preview: 'Náhľad',
  previewHide: 'Skryť náhľad',
  previewing: 'Načítava sa náhľad…',
  previewEmpty: 'Zdroj nevrátil žiadne údaje.',
  previewDenied: 'Tento zdroj nemôžete čítať.',
  previewChanged: 'Táto verzia sa zmenila. Otvorte ju znova a skúste to ešte raz.',
  previewUnavailable: 'Náhľad momentálne nie je k dispozícii.',
  previewMore: 'Zobrazujú sa iba prvé riadky.',
  previewRetry: 'Skúsiť znova',
};

const sl: Copy = {
  heading: 'Viri',
  none: 'Ta različica ne bere nobenih virov.',
  renew: 'Obnovi dostop',
  refreshHeading: 'Čas osvežitve',
  refreshDaily: 'Vsak dan ob {time} ({zone})',
  timeLabel: 'Čas',
  zoneLabel: 'Časovni pas',
  refreshSave: 'Shrani čas osvežitve',
  working: 'Shranjevanje …',
  saved: 'Shranjeno. Odpiranje nove različice …',
  failed: 'Ni uspelo. Nič ni bilo spremenjeno. Poskusite znova.',
  unconfirmed: 'Nismo mogli potrditi, da je shranjeno.',
  checkAgain: 'Preveri znova',
  invalidTime: 'Izberite čas in časovni pas.',
  preview: 'Predogled',
  previewHide: 'Skrij predogled',
  previewing: 'Nalaganje predogleda …',
  previewEmpty: 'Vir ni vrnil podatkov.',
  previewDenied: 'Tega vira ne morete brati.',
  previewChanged: 'Ta različica se je spremenila. Znova jo odprite in poskusite še enkrat.',
  previewUnavailable: 'Predogled trenutno ni na voljo.',
  previewMore: 'Prikazane so samo prve vrstice.',
  previewRetry: 'Poskusi znova',
};

const sv: Copy = {
  heading: 'Källor',
  none: 'Den här versionen läser inga källor.',
  renew: 'Förnya åtkomst',
  refreshHeading: 'Uppdateringstid',
  refreshDaily: 'Varje dag kl. {time} ({zone})',
  timeLabel: 'Tid',
  zoneLabel: 'Tidszon',
  refreshSave: 'Spara uppdateringstid',
  working: 'Sparar…',
  saved: 'Sparat. Öppnar den nya versionen…',
  failed: 'Det gick inte. Inget ändrades. Försök igen.',
  unconfirmed: 'Vi kunde inte bekräfta att det sparades.',
  checkAgain: 'Kontrollera igen',
  invalidTime: 'Välj en tid och en tidszon.',
  preview: 'Förhandsvisning',
  previewHide: 'Dölj förhandsvisning',
  previewing: 'Läser in förhandsvisningen…',
  previewEmpty: 'Källan returnerade inga data.',
  previewDenied: 'Du kan inte läsa den här källan.',
  previewChanged: 'Den här versionen har ändrats. Öppna den igen och försök på nytt.',
  previewUnavailable: 'Förhandsvisningen är inte tillgänglig just nu.',
  previewMore: 'Visar bara de första raderna.',
  previewRetry: 'Försök igen',
};

export const appSourcePlanCopy: NamespaceCatalog<AppSourcePlanCopyKey> = {
  en,
  bg,
  cs,
  da,
  de,
  el,
  es,
  et,
  fi,
  fr,
  ga,
  hr,
  hu,
  it,
  lt,
  lv,
  mt,
  nl,
  pl,
  pt,
  ro,
  sk,
  sl,
  sv,
};
export default appSourcePlanCopy;
